import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { MatrixedChartValues } from '../chartUtils';
import { LegendBaseProps } from './ChartLegend';

export interface BasicLegendProps extends LegendBaseProps {
  marker: 'value' | 'square' | 'circle';
  uppercaseLabel?: boolean;
}

type MarkerColors = { light: string; dark: string };

const BasicLegend = ({ marker, uppercaseLabel = false, data, colors, mouseOverCallback, mouseLeaveCallback }: BasicLegendProps) => {
  const isMatrixed = !!(typeof data.values[0] !== "number" && data.values[0]?.data);

  const Marker = ({ children, ...props }: { light: string; dark: string; children: any }) => {
    return marker === 'value' ? <Number {...props}>{children.toLocaleString()}</Number> : <Shape shape={marker} {...props} />;
  };

  const Label = ({ index, children }: { index: any; children: ReactNode }) => {
    return (
      <span className="label" id={`${index}`}>
        {children}
      </span>
    );
  };

  return isMatrixed ? (
    <Legend className="matrixed" textTransform={uppercaseLabel ? 'uppercase' : 'capitalize'}>
      {data.values.map((item, index) => {
        return (
          <Line key={`matrixed-item-${item}-${index}`} onMouseOver={() => mouseOverCallback(index)} onMouseLeave={() => mouseLeaveCallback()}>
            <Marker light={colors[index].start} dark={colors[index].end}>
              {(data.values as MatrixedChartValues[])[index].data.reduce((acc, curr) => {
                acc += curr;
                return acc;
              }, 0)}
            </Marker>
            <Label index={index}>{(item as MatrixedChartValues).label}</Label>
          </Line>
        );
      })}
    </Legend>
  ) : (
    <Legend className="non-matrixed" textTransform={uppercaseLabel ? 'uppercase' : 'capitalize'}>
      {data.labels.map((item, index) => {
        const colorIndex = index % colors.length;
        return (
          <Line key={`item-${item}-${index}`} onMouseOver={() => mouseOverCallback(index)} onMouseLeave={() => mouseLeaveCallback()}>
            <Marker light={colors[colorIndex].start} dark={colors[colorIndex].end}>
              {data.values[index]}
            </Marker>
            <Label index={index}>{item}</Label>
          </Line>
        );
      })}
    </Legend>
  );
};

export default BasicLegend;

const Legend = styled.div<{ textTransform: 'uppercase' | 'capitalize' }>`
  display: flex;
  flex-wrap: wrap;
  width: fit-content;
  align-items: flex-start;
  column-gap: ${({ theme }) => theme.spacing(3)};
  row-gap: ${({ theme }) => theme.spacing(1)};
  text-transform: ${({ textTransform }) => textTransform};

  &.matrixed {
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    font-size: 1.1rem;
    align-items: center;
  }

  &.non-matrixed {
    flex-direction: column;
    font-size: 1.5rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
      font-size: 1.1rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.values['lg']}px) {
      font-size: 1.3rem;
    }
  }
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  column-gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Number = styled.span<MarkerColors>`
  font-weight: 800;
  min-width: 60px;
  text-align: right;
  color: ${({ theme, light, dark }) => (theme.palette.mode === 'light' ? dark : light)};
`;

const Shape = styled.div<MarkerColors & { shape: 'square' | 'circle' }>`
  width: 15px;
  height: 15px;
  border-radius: ${({ shape }) => (shape === 'square' ? 'none' : '50%')};
  background-color: ${({ theme, light, dark }) => light};
`;
